import { cerrarSesion, recargarToken } from '@/lib/auth';
import { obtenerUsuarioActual } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { ArrowsClockwise, CaretDown, SignOut } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoAdelante } from './LogoAdelante';

const linkBase = 'text-sm transition-colors';
const linkInactive = 'text-zinc-400 hover:text-white';
const linkActive = 'text-white';

/** Estructura del menú superior: grupos desplegables y enlaces directos. */
interface ItemMenu {
  to: string;
  texto: string;
}
type EntradaMenu = { titulo: string; items: ItemMenu[] } | { titulo: string; to: string };

const MENU: EntradaMenu[] = [
  {
    titulo: 'Costos',
    items: [
      { to: '/costos', texto: 'Costos' },
      { to: '/costos-mensuales', texto: 'Costos Mensuales' },
      { to: '/pivot', texto: 'Pivot' },
      { to: '/costos-m2', texto: 'Costo vs Teórico' },
      { to: '/productividad', texto: 'Costos/m²' },
    ],
  },
  {
    titulo: 'Avance',
    items: [
      { to: '/avance', texto: 'Avance' },
      { to: '/mensual', texto: 'Venta/Indirectos' },
      { to: '/venta-vs-costo', texto: 'Venta vs Costo' },
    ],
  },
  {
    titulo: 'Maquinaria',
    items: [
      { to: '/maquinaria', texto: 'Maquinaria' },
      { to: '/maquinaria-pivot', texto: 'Pivot Maquinaria' },
      { to: '/maquinaria-general', texto: 'Análisis General Maq.' },
      { to: '/unidades-produccion', texto: 'Unidades de Producción' },
    ],
  },
  { titulo: 'Vivienda Gerencia', to: '/vivienda-gerencia' },
];

/**
 * Grupo desplegable del menú. Se abre con clic (o Enter/Espacio) y también al pasar
 * el mouse; se cierra con Esc, al hacer clic afuera o al navegar. El título se
 * resalta cuando la pantalla actual pertenece al grupo.
 */
function GrupoMenu({ titulo, items }: { titulo: string; items: ItemMenu[] }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const activo = items.some((i) => i.to === pathname);

  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setAbierto(true)}
      onMouseLeave={() => setAbierto(false)}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => setAbierto((v) => !v)}
        className={cn(
          linkBase,
          'flex items-center gap-1 py-4',
          activo || abierto ? linkActive : linkInactive,
        )}
      >
        {titulo}
        <CaretDown
          className={cn('h-3 w-3 transition-transform', abierto && 'rotate-180')}
          weight="bold"
        />
      </button>
      {abierto && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 min-w-[190px] rounded-b-md border border-zinc-800 bg-[#0A0A0A] py-1 shadow-lg"
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              onClick={() => setAbierto(false)}
              className={({ isActive }) =>
                cn(
                  'block whitespace-nowrap px-4 py-2 text-sm transition-colors hover:bg-zinc-900',
                  isActive ? 'text-white' : 'text-zinc-400 hover:text-white',
                )
              }
            >
              {it.texto}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/** Menú de usuario: nombre/email + Recargar token + Cerrar sesión. */
function MenuUsuario({
  iniciales,
  nombre,
  email,
}: {
  iniciales: string;
  nombre: string;
  email: string | null;
}) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [abierto]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground"
        aria-label={`Usuario: ${nombre}`}
        title={nombre}
      >
        {iniciales}
      </button>
      {abierto && (
        <div className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-800 shadow-lg">
          <div className="border-zinc-100 border-b px-4 py-3">
            <p className="font-semibold text-sm">{nombre}</p>
            {email && <p className="truncate text-xs text-muted-foreground">{email}</p>}
          </div>
          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              recargarToken();
            }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-zinc-50"
          >
            <ArrowsClockwise className="h-4 w-4" weight="bold" />
            <span>Recargar token</span>
            <span className="ml-auto text-[10px] text-muted-foreground">si la app se cuelga</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              cerrarSesion();
            }}
            className="flex w-full items-center gap-2 border-zinc-100 border-t px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
          >
            <SignOut className="h-4 w-4" weight="bold" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      )}
    </div>
  );
}

interface Props {
  /** Nombre de la app que aparece a la derecha del logo. Default: "BI Obras". */
  nombreApp?: string;
}

export function HeaderAdelante({ nombreApp = 'BI Obras' }: Props) {
  const usuario = obtenerUsuarioActual();
  const iniciales = usuario?.iniciales ?? '?';
  const nombreCompleto = usuario?.name ?? 'Sin sesión';
  const email = usuario?.username ?? null;

  return (
    <header className="bg-[#0A0A0A] text-white">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <LogoAdelante altura={22} />
          <span className="text-sm font-bold tracking-wider">ADELANTE.</span>
          {/* Nombre de la app: etiqueta tipo "chip", distinta de los links del menú. */}
          <span className="mr-3 whitespace-nowrap rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
            {nombreApp}
          </span>
        </div>

        <nav className="flex items-center gap-6" aria-label="Principal">
          {MENU.map((e) =>
            'items' in e ? (
              <GrupoMenu key={e.titulo} titulo={e.titulo} items={e.items} />
            ) : (
              <NavLink
                key={e.to}
                to={e.to}
                className={({ isActive }) =>
                  cn(linkBase, 'py-4', isActive ? linkActive : linkInactive)
                }
              >
                {e.titulo}
              </NavLink>
            ),
          )}
        </nav>

        <MenuUsuario iniciales={iniciales} nombre={nombreCompleto} email={email} />
      </div>
    </header>
  );
}
